import { useState } from "react";
import { Dialog } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { Alert } from "@/components/ui/Alert";
import { ApiError } from "@/lib/apiClient";
import {
  approveAbsenceRequest,
  formatAbsenceDateRange,
  getAbsenceTypeLabel,
  rejectAbsenceRequest,
} from "@/lib/employeeAbsences";
import type { PlanungAbsence } from "@/lib/montageplanung";
import { MP_DIALOG_Z_INDEX_CLASS } from "./layoutConstants";

export interface AbsenceApprovalDialogProps {
  open: boolean;
  absence: PlanungAbsence | null;
  onClose: () => void;
  onReviewed: () => void;
}

export function AbsenceApprovalDialog({
  open,
  absence,
  onClose,
  onReviewed,
}: AbsenceApprovalDialogProps) {
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState<"approve" | "reject" | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!absence) return null;

  const employeeName = absence.user_display?.trim() || absence.user_login;
  const dateLabel = formatAbsenceDateRange({
    ...absence,
    note: null,
    total_days: absence.day_fraction,
    requested_at: "",
    requested_by_login: null,
    reviewed_by_login: null,
    reviewed_by_display: null,
    reviewed_at: null,
    review_comment: null,
  });

  async function handleReview(action: "approve" | "reject") {
    if (absence!.id <= 0) return;
    setBusy(action);
    setError(null);
    try {
      const trimmed = comment.trim() || undefined;
      if (action === "approve") {
        await approveAbsenceRequest(absence!.id, trimmed);
      } else {
        await rejectAbsenceRequest(absence!.id, trimmed);
      }
      setComment("");
      onReviewed();
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Freigabe fehlgeschlagen.");
    } finally {
      setBusy(null);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Abwesenheitsantrag freigeben"
      size="sm"
      zIndexClass={MP_DIALOG_Z_INDEX_CLASS}
      footer={
        <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="secondary" onClick={onClose} disabled={busy != null}>
            Abbrechen
          </Button>
          <Button
            variant="danger"
            onClick={() => void handleReview("reject")}
            disabled={busy != null}
          >
            {busy === "reject" ? "Wird abgelehnt…" : "Ablehnen"}
          </Button>
          <Button onClick={() => void handleReview("approve")} disabled={busy != null}>
            {busy === "approve" ? "Wird freigegeben…" : "Freigeben"}
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        {error && <Alert variant="error">{error}</Alert>}
        <dl className="space-y-2 text-sm">
          <div>
            <dt className="text-label-tertiary">Mitarbeiter</dt>
            <dd className="font-medium text-label">{employeeName}</dd>
          </div>
          <div>
            <dt className="text-label-tertiary">Art</dt>
            <dd className="text-label">{getAbsenceTypeLabel(absence.absence_type)}</dd>
          </div>
          <div>
            <dt className="text-label-tertiary">Zeitraum</dt>
            <dd className="text-label">{dateLabel}</dd>
          </div>
        </dl>
        <label className="block text-sm">
          <span className="mb-1 block text-label-secondary">Kommentar (optional)</span>
          <textarea
            className="eh-input min-h-[72px] w-full resize-y"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Begründung oder Hinweis für den Mitarbeiter"
            disabled={busy != null}
          />
        </label>
      </div>
    </Dialog>
  );
}
